import { BRAND_SOURCE_KEY, BRAND_PERSON_NAME, BRAND_FAN_URL } from "@/lib/brandConfig";
/**
 * WhatsApp Broadcast Template
 * Mobile-first · primera persona · tono cálido y de comunidad
 *
 * Flujo de pago según país:
 * - AR: redirect directo a MercadoPago (VITE_WHATSAPP_BROADCAST_MP_URL)
 * - Internacional: Stripe Embedded Checkout "Miembro Basic" (699 USD cents)
 */

import { WHATSAPP_BROADCAST_CONFIG } from "@/config/whatsappBroadcastConfig";
import StripeCheckoutOverlay, { type CheckoutConfig } from "@/components/checkout/StripeCheckoutOverlay";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { useResolvedCtas, resolveNumField } from "@/hooks/useResolvedCtas";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import { Loader2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

/** Precio Stripe para usuarios internacionales — "Miembro Basic" */
const STRIPE_PLAN_AMOUNT_CENTS = 699;
const STRIPE_PLAN_PRODUCT_NAME = "Miembro Basic";
const STRIPE_PLAN_TIER = "basic";

const WHATSAPP_FLOW = "whatsapp_broadcast";

function newSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `wb_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

const WHATSAPP_SVG = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="w-full h-full">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const BENEFITS = [
  { emoji: "📲", text: "Te mando mensajes de mi día a día — lo que no subo a ningún otro lado" },
  { emoji: "💚", text: "Somos un grupo chico, tranquilo y con muy buena onda" },
  { emoji: "🙋‍♀️", text: "Soy yo quien escribe, no un equipo ni una agencia" },
  { emoji: "🌿", text: "Sin spam, sin publicidad — solo cosas que me importan de verdad" },
];

export default function WhatsAppBroadcastTemplate() {
  const navigate = useNavigate();
  const { country, language } = useTemplateSelection();
  const { ctas } = useResolvedCtas();
  const [isLoading, setIsLoading] = useState(false);

  // Precio sobreescribible desde CTAManager → whatsapp_broadcast sin redeploy
  const priceARS = resolveNumField(ctas.whatsapp_broadcast, "price_ars", WHATSAPP_BROADCAST_CONFIG.priceARS);
  const priceCents = resolveNumField(ctas.whatsapp_broadcast, "price_cents", STRIPE_PLAN_AMOUNT_CENTS);
  const [stripeCheckoutConfig, setStripeCheckoutConfig] = useState<CheckoutConfig | null>(null);
  const sessionIdRef = useRef(newSessionId());

  const isAR = country === "AR";
  const mpUrl = WHATSAPP_BROADCAST_CONFIG.mpCheckoutUrl;
  const successUrl = WHATSAPP_BROADCAST_CONFIG.successUrl || `${BRAND_FAN_URL}/r/igcanal`;

  /** PostHog + `/api/track-funnel-event` → DB admin + Slack (#anaafernandez). */
  const trackCta = useCallback(
    (eventName: string, props?: Record<string, unknown>) => {
      if (typeof window === "undefined") return;
      const pagePath = window.location.pathname;
      const pageUrl = window.location.href;
      const ph = (
        window as unknown as {
          posthog?: {
            get_distinct_id?: () => string;
          };
        }
      ).posthog;
      const fullProps = {
        flow: WHATSAPP_FLOW,
        country,
        language_worker: language,
        page_path: pagePath,
        page_url: pageUrl,
        source: "whatsapp_broadcast_page",
        ...props,
      };
      mirrorFunnelEvent({
        event_name: eventName,
        posthog_distinct_id: ph?.get_distinct_id?.(),
        session_id: sessionIdRef.current,
        template: "WhatsAppBroadcast",
        template_variant: isAR ? "ar_mp_redirect" : "intl_stripe_embedded",
        flow_name: WHATSAPP_FLOW,
        landing_path: pagePath,
        country,
        language: language?.split("-")[0],
        props: fullProps,
      });
    },
    [country, isAR, language],
  );

  const handleSubscribe = () => {
    if (isAR) {
      // AR: registro primero → luego el equipo envía el link de MercadoPago
      trackCta("whatsapp_broadcast_button_click", {
        destination: "registro_pre_mp",
        gateway: "mercadopago",
        funnel_step: "navigate_to_registro",
      });
      navigate("/registro?from_basic=1&from_whatsapp=1&plan_tier=basic");
      return;
    }

    // Internacional: Stripe Embedded Checkout "Miembro Basic"
    trackCta("whatsapp_broadcast_button_click", {
      destination: "stripe_embedded_overlay",
      gateway: "stripe",
      amount_cents: STRIPE_PLAN_AMOUNT_CENTS,
      currency: "USD",
      funnel_step: "open_checkout_overlay",
    });
    setStripeCheckoutConfig({
      amountCents: priceCents,
      productName: STRIPE_PLAN_PRODUCT_NAME,
      country,
      returnUrl: successUrl,
      metadata: {
        source: BRAND_SOURCE_KEY,
        plan_tier: STRIPE_PLAN_TIER,
        flow: "whatsapp_broadcast",
        product_name: STRIPE_PLAN_PRODUCT_NAME,
      },
    });
  };

  const bgStyle = WHATSAPP_BROADCAST_CONFIG.backgroundImageUrl
    ? {
        backgroundImage: `url(${WHATSAPP_BROADCAST_CONFIG.backgroundImageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : { background: "linear-gradient(160deg, #f0fdf4 0%, #fdf2f8 50%, #eff6ff 100%)" };

  return (
    <>
      <div className="min-h-screen w-full antialiased" style={bgStyle}>
        {/* Overlay cuando hay imagen */}
        {WHATSAPP_BROADCAST_CONFIG.backgroundImageUrl && (
          <div className="fixed inset-0 bg-white/60 pointer-events-none" />
        )}

        <div className="relative min-h-screen flex flex-col">

          {/* ── Header ─────────────────────────────────────────────── */}
          <header className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-green-100/80">
            <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
              <button
                onClick={() => {
                  trackCta("whatsapp_broadcast_nav_home_click", {
                    placement: "header",
                    destination: "/",
                  });
                  navigate("/");
                }}
                aria-label="Volver al inicio"
                className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Volver
              </button>

              <div className="flex items-center gap-2">
                <span className="w-5 h-5 text-[#25D366]">{WHATSAPP_SVG}</span>
                <span className="text-sm font-semibold text-gray-800">Mi canal de WhatsApp</span>
              </div>

              <div className="w-14" /> {/* spacer */}
            </div>
          </header>

          {/* ── Contenido principal ─────────────────────────────────── */}
          <main className="flex-1 max-w-lg mx-auto w-full px-4 py-8 space-y-8">

            {/* Hero */}
            <div className="text-center space-y-4">
              {/* Avatar */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#25D366] to-emerald-400 flex items-center justify-center shadow-lg shadow-green-200/60 p-4">
                    <span className="text-white">{WHATSAPP_SVG}</span>
                  </div>
                  <span className="absolute -bottom-1 -right-1 text-xl leading-none" role="img" aria-label="corazón">
                    💚
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight tracking-tight">
                  Hola 👋 ¡Sumate a mi<br />
                  <span className="text-[#25D366]">canal de WhatsApp!</span>
                </h1>
                <p className="text-gray-500 text-sm font-medium">
                  {isAR
                    ? "Argentina · Suscripción mensual"
                    : "Disponible en todo el mundo · Monthly subscription"}
                </p>
              </div>

              <p className="text-gray-600 text-base sm:text-lg leading-relaxed max-w-sm mx-auto">
                Es mi canal personal donde mando mensajes de mi día a día.{" "}
                <strong className="text-gray-800 font-semibold">No es un bot, soy yo.</strong>{" "}
                Sin algoritmos, sin publicidad — solo cosas reales.
              </p>
            </div>

            {/* Beneficios */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-green-100 p-5 space-y-4 shadow-sm">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                ¿Qué vas a recibir?
              </p>
              {BENEFITS.map((b, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="text-xl leading-none mt-0.5 flex-shrink-0">{b.emoji}</span>
                  <span className="text-gray-700 text-sm sm:text-base leading-snug">{b.text}</span>
                </div>
              ))}
            </div>

            {/* Quote */}
            <blockquote className="bg-white/70 backdrop-blur-sm border-l-4 border-[#25D366] rounded-r-2xl px-5 py-4">
              <p className="text-gray-600 text-sm sm:text-base italic leading-relaxed">
                "Quería un lugar donde poder estar de verdad con la gente que me sigue.
                Sin likes, sin views — solo escribir y que llegue directo."
              </p>
              <footer className="mt-2 text-[#25D366] font-semibold text-sm">
                — {BRAND_PERSON_NAME}
              </footer>
            </blockquote>

            {/* ── Card de suscripción ──────────────────────────────── */}
            <div className="bg-white rounded-3xl shadow-xl shadow-green-100/60 border border-green-50 overflow-hidden">

              {/* Banner precio */}
              <div className="bg-gradient-to-r from-[#25D366] to-emerald-400 px-6 py-5 text-center">
                <p className="text-white/90 text-sm font-semibold mb-1">
                  ¿Cuánto cuesta unirse?
                </p>
                {isAR ? (
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-white">
                      ${priceARS.toLocaleString("es-AR")}
                    </span>
                    <span className="text-white/80 text-sm">ARS por mes</span>
                  </div>
                ) : (
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-white">
                      ${(priceCents / 100).toFixed(2)}
                    </span>
                    <span className="text-white/80 text-sm">USD / month</span>
                  </div>
                )}
                <p className="text-white/75 text-xs mt-1.5">
                  Cancelás cuando querés — sin preguntas, sin drama
                </p>
              </div>

              <div className="p-6 space-y-5">

                {/* CTA */}
                <button
                  onClick={handleSubscribe}
                  disabled={isLoading}
                  className="w-full bg-[#25D366] hover:bg-[#20c05a] active:scale-[0.98] text-white font-bold text-base py-4 px-6 rounded-2xl transition-all flex items-center justify-center gap-2.5 shadow-lg shadow-green-200/60 disabled:opacity-50 disabled:cursor-not-allowed min-h-[56px]"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Redirigiendo...
                    </>
                  ) : (
                    <>
                      <span className="text-xl leading-none">💚</span>
                      ¡Quiero unirme!
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center leading-relaxed">
                  {isAR
                    ? "Creá tu cuenta y en breve te enviamos el link de MercadoPago para completar tu suscripción al canal."
                    : "You'll be taken to our secure checkout. After payment, I'll send you the WhatsApp channel link directly."}
                </p>
              </div>
            </div>

            {/* Badges de confianza */}
            <div className="flex flex-wrap items-center justify-center gap-2">
              {isAR ? (
                <>
                  {[
                    { icon: "🔒", label: "Pago seguro por Mercado Pago" },
                    { icon: "🔄", label: "Cancelás cuando querés" },
                    { icon: "⚡", label: "Link de MP en el día" },
                  ].map((b, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 bg-white/80 border border-green-100 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-full"
                    >
                      <span>{b.icon}</span>
                      {b.label}
                    </span>
                  ))}
                </>
              ) : (
                <>
                  {[
                    { icon: "🔒", label: "Secure Stripe checkout" },
                    { icon: "🔄", label: "Cancel anytime" },
                    { icon: "⚡", label: "Instant access after payment" },
                  ].map((b, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 bg-white/80 border border-green-100 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-full"
                    >
                      <span>{b.icon}</span>
                      {b.label}
                    </span>
                  ))}
                </>
              )}
            </div>
          </main>

          {/* ── Footer ─────────────────────────────────────────────── */}
          <footer className="max-w-lg mx-auto w-full px-4 pb-8 pt-4 text-center space-y-2">
            <button
              onClick={() => {
                trackCta("whatsapp_broadcast_nav_home_click", {
                  placement: "footer",
                  destination: "/",
                });
                navigate("/");
              }}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              ← Volver al inicio
            </button>
            <p className="text-xs text-gray-300">
              © {new Date().getFullYear()} {BRAND_PERSON_NAME}. Todos los derechos reservados.
            </p>
          </footer>

        </div>
      </div>

      {/* Stripe Embedded Checkout (internacional) */}
      <StripeCheckoutOverlay
        open={!!stripeCheckoutConfig}
        config={stripeCheckoutConfig}
        onClose={() => setStripeCheckoutConfig(null)}
      />
    </>
  );
}
